import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormControl, Validators } from '@angular/forms';
import { UserService } from 'src/app/core/services/user-service.service';
import { SnackbarService } from 'src/app/core/services/snackbar.service';
import { OrganizationType, organizationTypes } from 'src/app/core/helpers/constants';


interface ProfileData {
    name: string;
    email: string;
    individualName: string;
    organisationName: string;
    organisationType: OrganizationType['value'];
    credits: number;
    isProfileCompleted: boolean;
    createdAt: string;
    updatedAt: string;
}

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: ['./user-profile.component.scss']
})
export class UserProfileComponent implements OnInit {
    profileForm!: FormGroup;
    isEditing = false;
    isLoading = false;
    profileData: ProfileData = {
        name: '',
        email: '',
        individualName: '',
        organisationName: '',
        organisationType: '',
        credits: 0,
        isProfileCompleted: false,
        createdAt: '',
        updatedAt: ''
    };
    orgTypes = organizationTypes;

    constructor(
        private fb: FormBuilder,
        private userService: UserService,
        private snackbarService: SnackbarService,
    ) { }

    ngOnInit(): void {
        this.initForm();
        this.loadProfile();
    }

    initForm(): void {
        this.profileForm = this.fb.group({
            name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
            email: [{ value: '', disabled: true }],
            individualName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
            organisationName: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(100)]],
            organisationType: ['', Validators.required]
        });
    }

    loadProfile(): void {
        // Check from storage first
        // const user = this.userService.getUserDetails();
        // if (user && Object.keys(user).length > 0) {
        //     this.setProfileDataFromUser(user);
        // }

        this.userService.getUserProfile().subscribe({
            next: (response: any) => {
                if (response.data) {
                    this.setProfileDataFromUser(response.data?.user);
                    // Set editing mode if profile is not completed
                    const notCompleted = !this.profileData.isProfileCompleted;
                    if (notCompleted) {
                        this.startEditing();
                    }
                }
            },
            error: (error) => {
                console.error('Failed to load profile:', error);
                this.snackbarService.showError('Failed to load profile');
            }
        });
    }

    setProfileDataFromUser(user: any): void {
        const userData = "user" in user ? user.user : user;

        const orgType = this.orgTypes.find(type => type.value === userData.organisationType)?.value || '';
        this.profileData = {
            name: userData.name || '',
            email: userData.email || '',
            individualName: userData.individualName || '',
            organisationName: userData.organisationName || '',
            organisationType: orgType || '',
            credits: userData.credits || 0,
            isProfileCompleted: userData.isProfileCompleted || false,
            createdAt: userData.createdAt || '',
            updatedAt: userData.updatedAt || ''
        };

        this.profileForm.patchValue({
            name: this.profileData.name,
            email: this.profileData.email,
            individualName: this.profileData.individualName,
            organisationName: this.profileData.organisationName,
            organisationType: this.profileData.organisationType
        });

        // Disable/enable form based on editing state
        if (this.isEditing) {
            this.profileForm.enable();
            this.profileForm.get('email')?.disable();
        } else {
            this.profileForm.disable();
        }
    }

    handleProfileUpdate(): void {
        // Mark all fields as touched to show validation errors
        Object.keys(this.profileForm.controls).forEach(key => {
            this.profileForm.get(key)?.markAsTouched();
        });

        if (this.profileForm.invalid) {
            return;
        }

        this.isLoading = true;
        const formValue = this.profileForm.getRawValue();


        this.userService.updateUserProfile({
            name: formValue.name,
            individualName: formValue.individualName,
            organisationName: formValue.organisationName,
            organisationType: formValue.organisationType
        }).subscribe({
            next: (response: any) => {
                if (response.data) {
                    const userData = response.data?.user;
                    this.setProfileDataFromUser(userData);
                    this.userService.setUserDetails(userData);
                }
                this.isEditing = false;
                this.profileForm.disable(); // Disable form after successful save
                // Mark fields as untouched to clear errors
                Object.keys(this.profileForm.controls).forEach(key => {
                    this.profileForm.get(key)?.markAsUntouched();
                });
                this.snackbarService.showSuccess('Profile updated successfully!');
            },
            error: (error: any) => {
                this.isLoading = false;
                this.snackbarService.showError(
                    error.error?.message || 'Failed to update profile'
                );
            },
            complete: () => {
                this.isLoading = false;
            },
        });
    }

    startEditing(): void {
        this.isEditing = true;
        this.profileForm.enable();
        this.profileForm.get('email')?.disable(); // Keep email disabled
    }

    cancelEdit(): void {
        const user = this.userService.getUserDetails();
        if (user) {
            this.setProfileDataFromUser(user);
        }
        this.isEditing = false;
        // Disable form when canceling edit
        this.profileForm.disable();
        // Mark fields as untouched to clear errors
        Object.keys(this.profileForm.controls).forEach(key => {
            this.profileForm.get(key)?.markAsUntouched();
        });
    }

    isFieldEmpty(fieldValue: string): boolean {
        return !fieldValue || fieldValue.trim() === '';
    }

    // FormControl getters for type safety
    get nameControl(): FormControl {
        return this.profileForm.get('name') as FormControl;
    }

    get individualNameControl(): FormControl {
        return this.profileForm.get('individualName') as FormControl;
    }

    get organisationNameControl(): FormControl {
        return this.profileForm.get('organisationName') as FormControl;
    }

    get organisationTypeControl(): FormControl {
        return this.profileForm.get('organisationType') as FormControl;
    }

    get emailControl(): FormControl {
        return this.profileForm.get('email') as FormControl;
    }
}

